(() => {
  if (window.bigshockedHalloweenContestReady) return;
  window.bigshockedHalloweenContestReady = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const normalizeIndex = (index, length) => ((index % length) + length) % length;
  const MAX_ORIGINAL_PHOTO_BYTES = 10 * 1024 * 1024;
  const ALLOWED_PHOTO_TYPES = new Set(['image/jpeg', 'image/png', 'image/heic', 'image/heif']);
  const ALLOWED_PHOTO_EXTENSION = /\.(?:jpe?g|png|heic)$/i;
  const isAllowedEntryPhoto = (file) => {
    const fileType = (file?.type || '').toLowerCase();
    return ALLOWED_PHOTO_EXTENSION.test(file?.name || '') && (!fileType || ALLOWED_PHOTO_TYPES.has(fileType));
  };

  const initShowcase = (root) => {
    if (!root || root.dataset.hwShowcaseReady === 'true') return;

    const track = root.querySelector('[data-hw-showcase-track]');
    const previousButton = root.querySelector('[data-hw-showcase-prev]');
    const nextButton = root.querySelector('[data-hw-showcase-next]');
    const slides = track ? Array.from(track.querySelectorAll('[data-hw-showcase-slide]')) : [];

    if (!track || slides.length === 0) return;
    root.dataset.hwShowcaseReady = 'true';
    slides.forEach((slide, index) => { slide.dataset.hwShowcaseIndex = String(index); });

    let autoplayTimer = 0;
    let scrollTimer = 0;
    let resizeTimer = 0;
    let autoplayStopped = false;
    let pointerDown = false;
    let pointerMoved = false;
    let pointerStartX = 0;
    let pointerStartScroll = 0;
    let suppressClickUntil = 0;
    let cycleWidth = 0;
    let loopStart = 0;
    let looping = false;

    const getMaxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);
    const updateButtons = () => {
      const maxScroll = getMaxScroll();
      [previousButton, nextButton].forEach((button) => {
        if (button) button.hidden = !looping && maxScroll <= 1;
      });
      if (previousButton) previousButton.disabled = !looping && track.scrollLeft <= 1;
      if (nextButton) nextButton.disabled = !looping && track.scrollLeft >= maxScroll - 1;
    };

    // Move by a complete repeated strip, preserving the exact visible crop of every photo.
    const rebasePosition = () => {
      if (!looping || cycleWidth <= 0) return;
      const position = track.scrollLeft;
      const target = loopStart + normalizeIndex(position - loopStart, cycleWidth);
      if (Math.abs(target - position) <= 1) return;
      track.scrollTo({ left: target, behavior: 'instant' });
      if (pointerDown) pointerStartScroll += track.scrollLeft - position;
    };

    const makeClone = (slide) => {
      const clone = slide.cloneNode(true);
      clone.dataset.hwShowcaseClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('a, button, [tabindex]').forEach((element) => { element.tabIndex = -1; });
      return clone;
    };

    const rebuildLoop = () => {
      const progress = cycleWidth > 0 ? normalizeIndex(track.scrollLeft - loopStart, cycleWidth) / cycleWidth : 0;
      window.clearTimeout(scrollTimer);
      track.querySelectorAll('[data-hw-showcase-clone]').forEach((clone) => clone.remove());
      const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 0;
      cycleWidth = slides[slides.length - 1].getBoundingClientRect().right - slides[0].getBoundingClientRect().left + gap;
      looping = slides.length > 1 && cycleWidth > 0 && track.clientWidth > 0;
      loopStart = 0;

      if (looping) {
        // Keep enough copies on either side for wide screens and uninterrupted touch scrolling.
        const repeatCount = Math.ceil(track.clientWidth * 3 / cycleWidth) + 2;
        const before = document.createDocumentFragment();
        const after = document.createDocumentFragment();
        for (let repeat = 0; repeat < repeatCount; repeat += 1) {
          slides.forEach((slide) => {
            before.appendChild(makeClone(slide));
            after.appendChild(makeClone(slide));
          });
        }
        track.insertBefore(before, slides[0]);
        track.appendChild(after);
        loopStart = slides[0].getBoundingClientRect().left - track.firstElementChild.getBoundingClientRect().left;
      }

      track.scrollTo({ left: loopStart + progress * cycleWidth, behavior: 'instant' });
      updateButtons();
    };

    const scrollByPage = (direction) => {
      rebasePosition();
      track.scrollTo({
        left: Math.max(0, Math.min(getMaxScroll(), track.scrollLeft + direction * track.clientWidth * 0.8)),
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    };

    const stopAutoplay = () => {
      window.clearInterval(autoplayTimer);
      autoplayTimer = 0;
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (autoplayStopped || root.dataset.autoplay !== 'true' || getMaxScroll() <= 1 || reducedMotion.matches) return;

      const speed = Math.max(3000, Number.parseInt(root.dataset.speed || '5000', 10));
      autoplayTimer = window.setInterval(() => {
        scrollByPage(1);
      }, speed);
    };

    const stopForInteraction = () => {
      autoplayStopped = true;
      stopAutoplay();
    };

    previousButton?.addEventListener('click', () => {
      stopForInteraction();
      scrollByPage(-1);
    });

    nextButton?.addEventListener('click', () => {
      stopForInteraction();
      scrollByPage(1);
    });

    track.addEventListener('scroll', () => {
      updateButtons();
      window.clearTimeout(scrollTimer);
      if (looping && (track.scrollLeft < track.clientWidth || track.scrollLeft > getMaxScroll() - track.clientWidth)) {
        rebasePosition();
      }
      scrollTimer = window.setTimeout(() => {
        if (!pointerDown) rebasePosition();
      }, 140);
    }, { passive: true });
    track.addEventListener('wheel', stopForInteraction, { passive: true });
    track.addEventListener('keydown', stopForInteraction);

    track.addEventListener('pointerdown', (event) => {
      stopForInteraction();
      if (event.pointerType === 'touch' || (event.button !== undefined && event.button !== 0)) return;
      pointerDown = true;
      pointerMoved = false;
      pointerStartX = event.clientX;
      pointerStartScroll = track.scrollLeft;
      track.classList.add('is-dragging');
      window.clearTimeout(scrollTimer);
      track.scrollTo({ left: pointerStartScroll, behavior: 'instant' });
    });

    track.addEventListener('pointermove', (event) => {
      if (!pointerDown) return;
      const distance = event.clientX - pointerStartX;
      if (!pointerMoved && Math.abs(distance) > 6) {
        pointerMoved = true;
        track.setPointerCapture(event.pointerId);
      }
      if (!pointerMoved) return;
      event.preventDefault();
      track.scrollLeft = pointerStartScroll - distance;
    });

    const endPointerDrag = (event) => {
      if (!pointerDown) return;
      pointerDown = false;
      track.classList.remove('is-dragging');

      if (pointerMoved) {
        suppressClickUntil = Date.now() + 350;
        rebasePosition();
      }
      if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
    };

    track.addEventListener('pointerup', endPointerDrag);
    track.addEventListener('pointercancel', endPointerDrag);
    track.addEventListener('lostpointercapture', endPointerDrag);
    track.addEventListener('pointerleave', (event) => {
      if (!track.hasPointerCapture(event.pointerId)) endPointerDrag(event);
    });

    root.addEventListener(
      'click',
      (event) => {
        if (Date.now() < suppressClickUntil && event.target.closest('[data-hw-showcase-trigger]')) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true
    );

    root.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-hw-showcase-trigger]');
      if (!trigger) return;
      event.preventDefault();
      stopForInteraction();
      const photoSlides = slides.filter((slide) => slide.querySelector('[data-hw-showcase-trigger]'));
      const index = photoSlides.findIndex((slide) => slide.dataset.hwShowcaseIndex === trigger.closest('[data-hw-showcase-slide]').dataset.hwShowcaseIndex);
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const photos = photoSlides.map((slide) => {
        const link = slide.querySelector('[data-hw-showcase-trigger]');
        return {
          src: (mobile && link.dataset.mobileSrc) || link.href,
          alt: slide.querySelector('img')?.alt || 'Past Halloween contest entry'
        };
      });
      ensureEntryViewer().open(trigger, photos, Math.max(0, index));
    });

    root.addEventListener('dragstart', (event) => event.preventDefault());
    root.addEventListener('mouseenter', stopAutoplay);
    root.addEventListener('mouseleave', startAutoplay);
    root.addEventListener('focusin', stopAutoplay);
    root.addEventListener('focusout', startAutoplay);

    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(rebuildLoop, 140);
    });

    rebuildLoop();
    startAutoplay();
  };

  const initVoting = (root) => {
    if (!root || root.dataset.hwVotingReady === 'true') return;

    const cards = Array.from(root.querySelectorAll('[data-hw-vote-entry]'));
    const status = root.querySelector('[data-hw-vote-status]');
    const endpoint = (root.dataset.apiUrl || '').trim().replace(/\/+$/, '');
    const contestId = (root.dataset.contestId || '').trim();
    const enabled = root.dataset.enabled === 'true' && endpoint !== '' && contestId !== '';

    if (cards.length === 0) return;
    root.dataset.hwVotingReady = 'true';

    const voteLabel = root.dataset.voteLabel || 'VOTE';
    const votingLabel = root.dataset.votingLabel || 'VOTING...';
    const votedLabel = root.dataset.votedLabel || 'VOTED';
    const unavailableLabel = root.dataset.unavailableLabel || 'VOTING SOON';
    const successMessage = root.dataset.successMessage || 'Thank you. Your vote has been counted.';
    const alreadyVotedMessage = root.dataset.alreadyVotedMessage || 'Your vote for today is confirmed and cannot be changed. Come back tomorrow to vote again.';
    const confirmTitle = root.dataset.confirmTitle || 'Confirm your vote';
    const confirmMessage = root.dataset.confirmMessage || "Are you sure you want to vote for %entry%? You can vote once per day, and today's vote cannot be changed after submission.";
    const confirmLabel = root.dataset.confirmLabel || 'CONFIRM VOTE';
    const cancelLabel = root.dataset.cancelLabel || 'CANCEL';
    const pollSeconds = Math.max(10, Number.parseInt(root.dataset.pollSeconds || '15', 10) || 15);
    const entryIds = cards.map((card) => card.dataset.entryId || '').filter(Boolean);
    const grid = root.querySelector('.hw-vote-grid');
    const preview = root.querySelector('[data-hw-vote-preview]');
    const updatePreview = () => {
      if (!preview || !grid) return;
      const clipped = grid.scrollHeight > preview.clientHeight + 1;
      preview.classList.toggle('is-clipped', clipped);
      const fadeHeight = Number.parseFloat(window.getComputedStyle(preview).getPropertyValue('--hw-preview-fade')) || 0;
      const visibleBottom = preview.getBoundingClientRect().bottom - fadeHeight;
      grid.querySelectorAll('button').forEach((button) => {
        button.inert = clipped && button.getBoundingClientRect().top >= visibleBottom;
      });
    };
    const previewObserver = preview && grid ? new ResizeObserver(updatePreview) : null;
    previewObserver?.observe(preview);
    previewObserver?.observe(grid);
    root.hwVotingCleanup = () => previewObserver?.disconnect();
    const originalOrder = new Map(cards.map((card, index) => [card, index]));
    const voterStorageKey = 'bigshocked-contest-voter-id';
    const voteStoragePrefix = `bigshocked-contest-vote:${contestId}:`;
    const getNewYorkVoteDate = () => {
      try {
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone: 'America/New_York',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }).formatToParts(new Date());
        const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
        return `${values.year}-${values.month}-${values.day}`;
      } catch (error) {
        return new Date().toISOString().slice(0, 10);
      }
    };
    const createVoterId = () => {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      const randomPart = window.crypto?.getRandomValues
        ? Array.from(window.crypto.getRandomValues(new Uint32Array(3)), (value) => value.toString(36)).join('-')
        : Math.random().toString(36).slice(2);
      return `v-${Date.now().toString(36)}-${randomPart}`.slice(0, 80);
    };
    const getVoterId = () => {
      let voterId = '';
      try {
        voterId = window.localStorage.getItem(voterStorageKey) || '';
      } catch (error) {
        voterId = '';
      }
      if (/^[A-Za-z0-9][A-Za-z0-9_-]{0,79}$/.test(voterId)) return voterId;

      voterId = createVoterId();
      try {
        window.localStorage.setItem(voterStorageKey, voterId);
      } catch (error) {
        // Keep the generated ID for this page view when storage is unavailable.
      }
      return voterId;
    };
    const voterId = getVoterId();
    let voteDate = getNewYorkVoteDate();
    let storageKey = `${voteStoragePrefix}${voteDate}`;
    let chosenEntryId = '';
    let submitting = false;
    let confirmationPending = false;
    let loadingCounts = false;
    let pollTimer = 0;
    let rankedCards = [...cards];
    let rankedColumnCount = 0;
    let voteResizeTimer = 0;
    let closeActiveConfirmation = null;

    const readStoredVote = () => {
      try {
        return window.localStorage.getItem(storageKey) || '';
      } catch (error) {
        return '';
      }
    };
    const storeVote = (entryId) => {
      try {
        window.localStorage.setItem(storageKey, entryId);
      } catch (error) {
        // The current page still locks the vote when storage is unavailable.
      }
    };
    const syncVoteDate = () => {
      const nextVoteDate = getNewYorkVoteDate();
      if (nextVoteDate === voteDate) return false;

      voteDate = nextVoteDate;
      storageKey = `${voteStoragePrefix}${voteDate}`;
      chosenEntryId = readStoredVote();
      return true;
    };

    chosenEntryId = readStoredVote();

    const setStatus = (message = '', state = '') => {
      if (!status) return;
      status.textContent = message;
      status.classList.toggle('is-success', state === 'success');
      status.classList.toggle('is-error', state === 'error');
    };

    const setButtonLabel = (button, label) => {
      const labelElement = button?.querySelector('[data-hw-vote-button-label]');
      if (labelElement) labelElement.textContent = label;
    };

    const applyVoteState = () => {
      cards.forEach((card) => {
        const button = card.querySelector('[data-hw-vote-button]');
        const isChosen = chosenEntryId !== '' && card.dataset.entryId === chosenEntryId;

        card.classList.toggle('is-voted', isChosen);
        if (!button) return;

        button.disabled = !enabled || submitting || confirmationPending || chosenEntryId !== '';
        button.setAttribute('aria-disabled', button.disabled ? 'true' : 'false');
        button.setAttribute('aria-pressed', isChosen ? 'true' : 'false');
        setButtonLabel(button, !enabled ? unavailableLabel : isChosen ? votedLabel : voteLabel);
      });
    };

    const confirmVote = (entryTitle, returnFocus) => new Promise((resolve) => {
      const modal = document.createElement('div');
      const idSuffix = Math.random().toString(36).slice(2, 10);
      const titleId = `HalloweenVoteConfirmTitle-${idSuffix}`;
      const copyId = `HalloweenVoteConfirmCopy-${idSuffix}`;
      modal.className = 'hw-vote-confirm';
      modal.innerHTML = `
        <div class="hw-vote-confirm__backdrop" data-hw-vote-confirm-cancel></div>
        <div class="hw-vote-confirm__dialog" role="dialog" aria-modal="true" aria-labelledby="${titleId}" aria-describedby="${copyId}">
          <h2 class="hw-vote-confirm__title" id="${titleId}"></h2>
          <p class="hw-vote-confirm__copy" id="${copyId}"></p>
          <div class="hw-vote-confirm__actions">
            <button class="hw-vote-confirm__button hw-vote-confirm__button--cancel" type="button" data-hw-vote-confirm-cancel></button>
            <button class="hw-vote-confirm__button hw-vote-confirm__button--confirm" type="button" data-hw-vote-confirm-submit></button>
          </div>
        </div>`;

      const dialog = modal.querySelector('.hw-vote-confirm__dialog');
      const cancelButton = modal.querySelector('.hw-vote-confirm__button--cancel');
      const confirmButton = modal.querySelector('.hw-vote-confirm__button--confirm');
      modal.querySelector('.hw-vote-confirm__title').textContent = confirmTitle;
      modal.querySelector('.hw-vote-confirm__copy').textContent = confirmMessage.replace('%entry%', entryTitle);
      cancelButton.textContent = cancelLabel;
      confirmButton.textContent = confirmLabel;

      let closed = false;
      const close = (confirmed, restoreFocus = true) => {
        if (closed) return;
        closed = true;
        document.removeEventListener('keydown', handleKeydown);
        document.documentElement.classList.remove('hw-vote-confirm-open');
        modal.remove();
        closeActiveConfirmation = null;
        if (restoreFocus && returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
        resolve(confirmed);
      };
      const handleKeydown = (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          close(false);
          return;
        }
        if (event.key !== 'Tab') return;

        const focusable = [cancelButton, confirmButton];
        const currentIndex = focusable.indexOf(document.activeElement);
        const nextIndex = event.shiftKey
          ? (currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1)
          : (currentIndex >= focusable.length - 1 ? 0 : currentIndex + 1);
        event.preventDefault();
        focusable[nextIndex].focus();
      };

      modal.addEventListener('click', (event) => {
        if (event.target.closest('[data-hw-vote-confirm-submit]')) close(true, false);
        else if (event.target.closest('[data-hw-vote-confirm-cancel]')) close(false);
      });
      closeActiveConfirmation = (restoreFocus = false) => close(false, restoreFocus);
      document.body.appendChild(modal);
      document.documentElement.classList.add('hw-vote-confirm-open');
      document.addEventListener('keydown', handleKeydown);
      window.requestAnimationFrame(() => {
        modal.classList.add('is-visible');
        confirmButton.focus({ preventScroll: true });
      });
    });

    const getRankedColumnCount = () => {
      if (window.matchMedia('(max-width: 749px)').matches) return 2;
      if (window.matchMedia('(max-width: 989px)').matches) return 3;
      return 4;
    };

    const renderRankedCards = () => {
      if (!grid) return;

      const columnCount = getRankedColumnCount();
      const columns = Array.from({ length: columnCount }, () => {
        const column = document.createElement('div');
        column.className = 'hw-vote-column';
        return column;
      });
      const visibleCards = rankedCards.filter((card) => !card.hidden && !card.classList.contains('hw-vote-card--event-hidden'));
      const hiddenCards = rankedCards.filter((card) => card.hidden || card.classList.contains('hw-vote-card--event-hidden'));
      const fragment = document.createDocumentFragment();

      columns.forEach((column) => fragment.appendChild(column));
      grid.classList.add('is-ranked');
      grid.replaceChildren(fragment);

      visibleCards.forEach((card, index) => {
        const targetColumn = index < columnCount
          ? columns[index]
          : columns.reduce((shortest, column) => column.scrollHeight < shortest.scrollHeight ? column : shortest);
        targetColumn.appendChild(card);
      });
      hiddenCards.forEach((card) => grid.appendChild(card));
      rankedColumnCount = columnCount;
      updatePreview();
    };

    const updateCounts = (counts = {}) => {
      cards.forEach((card) => {
        const countElement = card.querySelector('[data-hw-vote-count]');
        const count = Number(counts[card.dataset.entryId] || 0);
        const safeCount = Number.isFinite(count) ? Math.max(0, count) : 0;

        card.dataset.hwVoteCount = String(safeCount);
        if (countElement) countElement.textContent = safeCount.toLocaleString();
      });

      if (!grid) return;

      rankedCards = [...cards].sort((firstCard, secondCard) => {
        const countDifference = Number(secondCard.dataset.hwVoteCount || 0) - Number(firstCard.dataset.hwVoteCount || 0);
        return countDifference || originalOrder.get(firstCard) - originalOrder.get(secondCard);
      });
      renderRankedCards();
    };

    const apiUrl = (path) => {
      const url = new URL(`${endpoint}${path}`);
      return url;
    };

    const readResponse = async (response) => {
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = new Error(payload.error || 'The voting service could not complete this request.');
        error.status = response.status;
        error.payload = payload;
        throw error;
      }
      return payload;
    };

    const refreshCounts = async ({ announceError = false } = {}) => {
      if (syncVoteDate()) {
        applyVoteState();
        setStatus(chosenEntryId ? alreadyVotedMessage : '', chosenEntryId ? 'success' : '');
      }
      if (!enabled || loadingCounts || document.hidden) return;
      loadingCounts = true;

      try {
        const url = apiUrl('/votes');
        url.searchParams.set('contest', contestId);
        url.searchParams.set('entries', entryIds.join(','));
        const response = await fetch(url.toString(), {
          method: 'GET',
          mode: 'cors',
          cache: 'no-store',
          headers: { Accept: 'application/json' }
        });
        const payload = await readResponse(response);
        updateCounts(payload.counts);
      } catch (error) {
        if (announceError) setStatus('Live vote totals are temporarily unavailable. Please try again.', 'error');
      } finally {
        loadingCounts = false;
      }
    };

    applyVoteState();

    if (!enabled) {
      updateCounts();
      return;
    }

    if (chosenEntryId) setStatus(alreadyVotedMessage, 'success');
    refreshCounts({ announceError: true });
    pollTimer = window.setInterval(refreshCounts, pollSeconds * 1000);

    root.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-hw-vote-button]');
      if (!button || !root.contains(button) || submitting) return;

      const card = button.closest('[data-hw-vote-entry]');
      const entryId = card?.dataset.entryId || '';
      if (!entryId) return;

      if (syncVoteDate()) applyVoteState();
      if (chosenEntryId) {
        setStatus(alreadyVotedMessage, 'success');
        return;
      }

      const entryTitle = card.querySelector('h3')?.textContent?.trim() || 'this entry';
      confirmationPending = true;
      applyVoteState();
      const confirmed = await confirmVote(entryTitle, button);
      confirmationPending = false;
      if (!confirmed) {
        applyVoteState();
        return;
      }

      submitting = true;
      applyVoteState();
      setButtonLabel(button, votingLabel);
      setStatus('');

      try {
        const response = await fetch(apiUrl('/vote').toString(), {
          method: 'POST',
          mode: 'cors',
          cache: 'no-store',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ contestId, entryId, voterId })
        });
        const payload = await readResponse(response);
        updateCounts(payload.counts || {});

        if (payload.voteDate && /^\d{4}-\d{2}-\d{2}$/.test(payload.voteDate)) {
          voteDate = payload.voteDate;
          storageKey = `${voteStoragePrefix}${voteDate}`;
        }
        chosenEntryId = entryId;
        storeVote(chosenEntryId);
        setStatus(successMessage, 'success');
      } catch (error) {
        if (error.status === 409) {
          if (error.payload?.counts) updateCounts(error.payload.counts);
          if (error.payload?.voteDate && /^\d{4}-\d{2}-\d{2}$/.test(error.payload.voteDate)) {
            voteDate = error.payload.voteDate;
            storageKey = `${voteStoragePrefix}${voteDate}`;
          }
          chosenEntryId = error.payload?.entryId || entryId;
          storeVote(chosenEntryId);
          setStatus(error.message || alreadyVotedMessage, 'success');
        } else {
          setStatus(error.message || 'Your vote could not be submitted. Please try again.', 'error');
        }
      } finally {
        submitting = false;
        applyVoteState();
      }
    });

    const handleStorage = (event) => {
      if (event.key !== storageKey) return;
      chosenEntryId = event.newValue || '';
      applyVoteState();
      setStatus(chosenEntryId ? alreadyVotedMessage : '', chosenEntryId ? 'success' : '');
    };
    window.addEventListener('storage', handleStorage);

    const handleResize = () => {
      window.clearTimeout(voteResizeTimer);
      voteResizeTimer = window.setTimeout(() => {
        if (getRankedColumnCount() !== rankedColumnCount) renderRankedCards();
      }, 120);
    };
    window.addEventListener('resize', handleResize);

    root.hwVotingCleanup = () => {
      previewObserver?.disconnect();
      closeActiveConfirmation?.(false);
      window.clearInterval(pollTimer);
      window.clearTimeout(voteResizeTimer);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('resize', handleResize);
    };
  };

  const loadPhotoSource = async (file) => {
    if ('createImageBitmap' in window) {
      try {
        return await window.createImageBitmap(file, { imageOrientation: 'from-image' });
      } catch (error) {
        // Fall back to an HTML image for browsers with partial image-bitmap support.
      }
    }

    return new Promise((resolve, reject) => {
      const image = new Image();
      const objectUrl = URL.createObjectURL(file);
      image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(image);
      };
      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('This photo format cannot be read by your browser. Please upload a JPG, PNG, or HEIC image.'));
      };
      image.src = objectUrl;
    });
  };

  const canvasToBlob = (canvas, type, quality) => new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('The photo could not be prepared. Please try a different image.'));
    }, type, quality);
  });

  const optimizeEntryPhoto = async (file, maxBytes) => {
    const directlySupportedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const fileType = (file.type || '').toLowerCase();

    if (directlySupportedTypes.includes(fileType) && file.size <= maxBytes) return file;

    const source = await loadPhotoSource(file);
    const sourceWidth = source.width || source.naturalWidth || 0;
    const sourceHeight = source.height || source.naturalHeight || 0;

    if (!sourceWidth || !sourceHeight) {
      source.close?.();
      throw new Error('The selected photo has invalid dimensions. Please choose another image.');
    }

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) {
      source.close?.();
      throw new Error('Your browser cannot prepare this photo. Please try another browser.');
    }

    let scale = Math.min(1, 2400 / Math.max(sourceWidth, sourceHeight));
    let quality = 0.88;
    let optimizedBlob = null;

    for (let attempt = 0; attempt < 8; attempt += 1) {
      canvas.width = Math.max(1, Math.round(sourceWidth * scale));
      canvas.height = Math.max(1, Math.round(sourceHeight * scale));
      context.fillStyle = '#000000';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(source, 0, 0, canvas.width, canvas.height);
      optimizedBlob = await canvasToBlob(canvas, 'image/jpeg', quality);

      if (optimizedBlob.size <= maxBytes) break;
      if (quality > 0.64) quality = Math.max(0.64, quality - 0.08);
      else scale *= 0.82;
    }

    source.close?.();

    if (!optimizedBlob || optimizedBlob.size > maxBytes) {
      throw new Error('This photo is still too large after processing. Please upload a smaller image.');
    }

    const baseName = (file.name || 'halloween-entry').replace(/\.[^.]+$/, '').slice(0, 80) || 'halloween-entry';
    return new File([optimizedBlob], `${baseName}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now()
    });
  };

  const initEntryForm = (form) => {
    if (!form || form.dataset.hwEntryFormReady === 'true') return;

    const endpoint = (form.dataset.apiUrl || '').trim().replace(/\/+$/, '');
    const contestId = (form.dataset.contestId || '').trim();
    const submitButton = form.querySelector('[data-hw-entry-submit]');
    const photoInput = form.querySelector('[data-hw-entry-photo]');
    const preview = form.querySelector('[data-hw-entry-photo-preview]');
    const status = form.querySelector('[data-hw-entry-status]');
    const tool = form.closest('.hw-entry-tool');
    const success = tool?.querySelector('[data-hw-entry-success]');
    const successMessage = success?.querySelector('[data-hw-entry-success-message]');
    const reference = success?.querySelector('[data-hw-entry-reference]');
    const submitAnotherButton = success?.querySelector('[data-hw-entry-again]');
    const maxPhotoBytes = Math.max(100000, Number.parseInt(form.dataset.maxPhotoBytes || '900000', 10));
    const maxPhotoCount = Math.max(1, Number.parseInt(form.dataset.maxPhotoCount || '20', 10));

    if (!endpoint || !contestId || !submitButton || !photoInput) return;
    form.dataset.hwEntryFormReady = 'true';

    const submitLabel = form.dataset.submitLabel || 'SUBMIT ENTRY';
    const submittingLabel = form.dataset.submittingLabel || 'SUBMITTING...';
    const optimizingLabel = form.dataset.optimizingLabel || 'OPTIMIZING PHOTO...';
    const defaultError = form.dataset.errorMessage || 'Your entry could not be submitted. Please check the form and try again.';
    let previewUrls = [];
    let selectedPhotos = [];
    let submitting = false;

    const setStatus = (message = '', state = '') => {
      if (!status) return;
      status.textContent = message;
      status.classList.toggle('is-error', state === 'error');
      status.classList.toggle('is-success', state === 'success');
    };

    const setSubmitting = (nextSubmitting, label = submitLabel) => {
      submitting = nextSubmitting;
      submitButton.disabled = nextSubmitting;
      submitButton.textContent = label;
      form.setAttribute('aria-busy', nextSubmitting ? 'true' : 'false');
    };

    const clearPreview = () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
      previewUrls = [];
      if (preview) preview.hidden = true;
      preview?.replaceChildren();
    };

    const syncPhotoInput = () => {
      const transfer = new DataTransfer();
      selectedPhotos.forEach((file) => transfer.items.add(file));
      photoInput.files = transfer.files;
    };

    const photoKey = (file) => [file.name, file.size, file.lastModified, file.type].join('::');

    const renderPhotoPreview = () => {
      clearPreview();
      if (preview && selectedPhotos.length > 0) {
        const fragment = document.createDocumentFragment();
        selectedPhotos.forEach((file, index) => {
          const item = document.createElement('figure');
          const image = document.createElement('img');
          const label = document.createElement('figcaption');
          const removeButton = document.createElement('button');
          const previewUrl = URL.createObjectURL(file);

          previewUrls.push(previewUrl);
          item.className = 'hw-photo-preview__item';
          image.src = previewUrl;
          image.alt = `Selected contest photo ${index + 1}`;
          image.loading = 'lazy';
          label.textContent = String(index + 1);
          removeButton.className = 'hw-photo-preview__remove';
          removeButton.type = 'button';
          removeButton.dataset.hwRemovePhotoIndex = String(index);
          removeButton.setAttribute('aria-label', `Remove selected photo ${index + 1}`);
          removeButton.title = `Remove photo ${index + 1}`;
          removeButton.innerHTML = '&times;';
          item.append(image, label, removeButton);
          fragment.appendChild(item);
        });
        preview.appendChild(fragment);
        preview.hidden = false;
      }
    };

    const restoreSelectedPhotos = () => {
      try {
        syncPhotoInput();
      } catch (error) {
        photoInput.value = '';
      }
    };

    const handlePhotoChange = () => {
      const incomingPhotos = Array.from(photoInput.files || []);
      if (incomingPhotos.length === 0) return;

      if (incomingPhotos.some((file) => !isAllowedEntryPhoto(file))) {
        const message = 'Please upload a JPG, PNG, or HEIC image.';
        restoreSelectedPhotos();
        photoInput.setCustomValidity(message);
        setStatus(message, 'error');
        photoInput.reportValidity();
        return;
      }

      if (incomingPhotos.some((file) => file.size >= MAX_ORIGINAL_PHOTO_BYTES)) {
        const message = 'Each original photo must be smaller than 10 MB.';
        restoreSelectedPhotos();
        photoInput.setCustomValidity(message);
        setStatus(message, 'error');
        photoInput.reportValidity();
        return;
      }

      const selectedKeys = new Set(selectedPhotos.map(photoKey));
      const uniqueIncomingPhotos = incomingPhotos.filter((file) => {
        const key = photoKey(file);
        if (selectedKeys.has(key)) return false;
        selectedKeys.add(key);
        return true;
      });
      const nextPhotos = [...selectedPhotos, ...uniqueIncomingPhotos];

      if (nextPhotos.length > maxPhotoCount) {
        const message = `Please select no more than ${maxPhotoCount} photos.`;
        restoreSelectedPhotos();
        photoInput.setCustomValidity(message);
        setStatus(message, 'error');
        photoInput.reportValidity();
        return;
      }

      selectedPhotos = nextPhotos;
      restoreSelectedPhotos();
      photoInput.setCustomValidity('');
      setStatus();
      renderPhotoPreview();
    };

    const handlePhotoRemove = (event) => {
      const removeButton = event.target.closest('[data-hw-remove-photo-index]');
      if (!removeButton) return;

      const removeIndex = Number.parseInt(removeButton.dataset.hwRemovePhotoIndex || '-1', 10);
      if (removeIndex < 0 || removeIndex >= selectedPhotos.length) return;

      selectedPhotos.splice(removeIndex, 1);
      restoreSelectedPhotos();
      photoInput.setCustomValidity('');
      setStatus();
      renderPhotoPreview();
    };

    const readResponse = async (response) => {
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || defaultError);
      return payload;
    };

    const handleSubmit = async (event) => {
      event.preventDefault();
      if (submitting || !form.reportValidity()) return;

      const photos = [...selectedPhotos];
      if (photos.length === 0) {
        photoInput.setCustomValidity('Please select at least one contest photo.');
        photoInput.reportValidity();
        return;
      }

      if (photos.length > maxPhotoCount) {
        const message = `Please select no more than ${maxPhotoCount} photos.`;
        photoInput.setCustomValidity(message);
        setStatus(message, 'error');
        photoInput.reportValidity();
        return;
      }

      if (photos.some((photo) => !isAllowedEntryPhoto(photo))) {
        const message = 'Please upload a JPG, PNG, or HEIC image.';
        photoInput.setCustomValidity(message);
        setStatus(message, 'error');
        photoInput.reportValidity();
        return;
      }

      if (photos.some((photo) => photo.size >= MAX_ORIGINAL_PHOTO_BYTES)) {
        const message = 'Each original photo must be smaller than 10 MB.';
        photoInput.setCustomValidity(message);
        setStatus(message, 'error');
        photoInput.reportValidity();
        return;
      }

      setStatus();
      setSubmitting(true, optimizingLabel);

      try {
        const optimizedPhotos = [];
        for (let index = 0; index < photos.length; index += 1) {
          setSubmitting(true, `${optimizingLabel} ${index + 1}/${photos.length}`);
          optimizedPhotos.push(await optimizeEntryPhoto(photos[index], maxPhotoBytes));
        }
        setSubmitting(true, submittingLabel);

        const body = new FormData(form);
        body.set('contestId', contestId);
        body.delete('photo');
        body.delete('photos');
        optimizedPhotos.forEach((photo) => body.append('photos', photo, photo.name));

        const response = await fetch(`${endpoint}/entry`, {
          method: 'POST',
          mode: 'cors',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
          body
        });
        const payload = await readResponse(response);

        form.hidden = true;
        if (success) success.hidden = false;
        if (successMessage) successMessage.textContent = form.dataset.successMessage || successMessage.textContent;
        if (reference && payload.reference) {
          reference.textContent = `Entry reference: ${payload.reference}`;
          reference.hidden = false;
        }
        selectedPhotos = [];
        clearPreview();
        form.reset();
        success?.focus?.({ preventScroll: true });
      } catch (error) {
        setStatus(error.message || defaultError, 'error');
      } finally {
        setSubmitting(false);
      }
    };

    const handleSubmitAnother = () => {
      if (success) success.hidden = true;
      if (reference) {
        reference.hidden = true;
        reference.textContent = '';
      }
      form.hidden = false;
      setStatus();
      form.querySelector('input:not([type="hidden"]):not([type="checkbox"])')?.focus({ preventScroll: true });
    };

    photoInput.addEventListener('change', handlePhotoChange);
    preview?.addEventListener('click', handlePhotoRemove);
    form.addEventListener('submit', handleSubmit);
    submitAnotherButton?.addEventListener('click', handleSubmitAnother);

    form.hwEntryCleanup = () => {
      clearPreview();
      photoInput.removeEventListener('change', handlePhotoChange);
      preview?.removeEventListener('click', handlePhotoRemove);
      form.removeEventListener('submit', handleSubmit);
      submitAnotherButton?.removeEventListener('click', handleSubmitAnother);
    };
  };

  let entryViewer = null;

  const ensureEntryViewer = () => {
    if (entryViewer) return entryViewer;

    const root = document.createElement('div');
    root.className = 'hw-entry-viewer';
    root.hidden = true;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Contest entry photos');
    root.innerHTML = `
      <button class="hw-entry-viewer__backdrop" type="button" data-hw-entry-viewer-close aria-label="Close photo viewer"></button>
      <div class="hw-entry-viewer__dialog">
        <button class="hw-entry-viewer__close" type="button" data-hw-entry-viewer-close aria-label="Close photo viewer">&times;</button>
        <button class="hw-entry-viewer__nav hw-entry-viewer__nav--previous" type="button" data-hw-entry-viewer-previous aria-label="Previous photo">&#8249;</button>
        <div class="hw-entry-viewer__stage">
          <img class="hw-entry-viewer__image" data-hw-entry-viewer-image alt="">
        </div>
        <button class="hw-entry-viewer__nav hw-entry-viewer__nav--next" type="button" data-hw-entry-viewer-next aria-label="Next photo">&#8250;</button>
        <p class="hw-entry-viewer__count" data-hw-entry-viewer-count aria-live="polite"></p>
      </div>`;
    document.body.appendChild(root);

    const image = root.querySelector('[data-hw-entry-viewer-image]');
    const count = root.querySelector('[data-hw-entry-viewer-count]');
    const previousButton = root.querySelector('[data-hw-entry-viewer-previous]');
    const nextButton = root.querySelector('[data-hw-entry-viewer-next]');
    const closeButton = root.querySelector('.hw-entry-viewer__close');
    let photos = [];
    let currentIndex = 0;
    let returnFocus = null;
    let touchStartX = null;

    const render = () => {
      if (photos.length === 0) return;
      currentIndex = normalizeIndex(currentIndex, photos.length);
      const photo = photos[currentIndex];
      image.src = photo.src;
      image.alt = photo.alt || `Contest entry photo ${currentIndex + 1}`;
      if (photo.srcset) image.srcset = photo.srcset;
      else image.removeAttribute('srcset');
      image.sizes = photo.sizes || '100vw';
      count.textContent = `${currentIndex + 1} / ${photos.length}`;
      const hasMultiple = photos.length > 1;
      previousButton.hidden = !hasMultiple;
      nextButton.hidden = !hasMultiple;
    };

    const move = (direction) => {
      if (photos.length < 2) return;
      currentIndex += direction;
      render();
    };

    const close = () => {
      if (root.hidden) return;
      root.hidden = true;
      document.documentElement.classList.remove('hw-entry-viewer-open');
      image.removeAttribute('src');
      image.removeAttribute('srcset');
      returnFocus?.focus?.({ preventScroll: true });
      photos = [];
    };

    root.querySelectorAll('[data-hw-entry-viewer-close]').forEach((button) => button.addEventListener('click', close));
    previousButton.addEventListener('click', () => move(-1));
    nextButton.addEventListener('click', () => move(1));
    root.addEventListener('touchstart', (event) => {
      touchStartX = event.changedTouches[0]?.clientX ?? null;
    }, { passive: true });
    root.addEventListener('touchend', (event) => {
      if (touchStartX === null) return;
      const distance = (event.changedTouches[0]?.clientX ?? touchStartX) - touchStartX;
      touchStartX = null;
      if (Math.abs(distance) >= 45) move(distance > 0 ? -1 : 1);
    }, { passive: true });
    document.addEventListener('keydown', (event) => {
      if (root.hidden) return;
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowLeft') move(-1);
      if (event.key === 'ArrowRight') move(1);
    });

    entryViewer = {
      open(trigger, galleryPhotos = null, startIndex = 0) {
        const template = trigger.closest('.hw-vote-card__media')?.querySelector('[data-hw-entry-gallery-template]');
        photos = galleryPhotos || (template
          ? Array.from(template.content.querySelectorAll('img')).map((source) => ({
              src: source.currentSrc || source.getAttribute('src') || '',
              srcset: source.getAttribute('srcset') || '',
              sizes: source.getAttribute('sizes') || '',
              alt: source.getAttribute('alt') || ''
            })).filter((photo) => photo.src)
          : []);
        if (photos.length === 0) return;

        currentIndex = startIndex;
        returnFocus = trigger;
        root.hidden = false;
        document.documentElement.classList.add('hw-entry-viewer-open');
        render();
        closeButton.focus({ preventScroll: true });
      },
      close
    };
    return entryViewer;
  };

  const sourceUrlWithPreviewContext = (source) => {
    const parsedSource = new URL(source || '/pages/halloween-dress-up-contest', window.location.origin);
    const url = new URL(`${parsedSource.pathname}${parsedSource.search}`, window.location.origin);
    const previewKeys = ['preview_theme_id', 'preview_key', '_fd', 'pb', 'oseid'];

    previewKeys.forEach((key) => {
      const value = new URL(window.location.href).searchParams.get(key);
      if (value && !url.searchParams.has(key)) url.searchParams.set(key, value);
    });

    return url;
  };

  const initAllEntriesGallery = async (root) => {
    if (!root || root.dataset.hwAllEntriesReady === 'true') return;

    root.dataset.hwAllEntriesReady = 'true';
    const state = root.querySelector('[data-hw-all-entries-state]');

    try {
      const sourceUrl = sourceUrlWithPreviewContext(root.dataset.sourceUrl);
      const response = await fetch(sourceUrl.toString(), {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'text/html' }
      });

      if (!response.ok) throw new Error(`Gallery source returned ${response.status}.`);

      const sourceDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const sourceVoting = sourceDocument.querySelector('[data-hw-voting]');
      if (!sourceVoting) throw new Error('Gallery source was not found.');

      const voting = sourceVoting.cloneNode(true);
      delete voting.dataset.hwVotingReady;
      const preview = voting.querySelector('[data-hw-vote-preview]');
      if (preview) preview.replaceWith(...preview.childNodes);
      voting.querySelectorAll('.hw-vote-card--event-hidden, .hw-vote-card[hidden]').forEach((card) => {
        card.classList.remove('hw-vote-card--event-hidden');
        card.removeAttribute('hidden');
        card.removeAttribute('aria-hidden');
      });
      voting.querySelector('.hw-gallery-more')?.remove();
      const voteStatus = voting.querySelector('[data-hw-vote-status]');
      if (voteStatus && voting.dataset.enabled === 'true') voteStatus.textContent = '';

      root.replaceChildren(voting);
      initVoting(voting);
    } catch (error) {
      if (!state) return;
      state.textContent = root.dataset.errorMessage || 'The contest gallery could not be loaded. Please refresh and try again.';
      state.classList.add('is-error');
    }
  };

  const swapShowcaseAndVotingSections = () => {
    const storyRoot = document.querySelector('[id^="HalloweenContestStory-"]');
    const entryAnchor = document.querySelector('#HalloweenEntry');
    const entryRoot = entryAnchor?.closest('.halloween-contest');
    const pastShowcase = storyRoot?.querySelector('[data-hw-showcase]')?.closest('section.hw-section');
    const prizeGallery = storyRoot?.querySelector('section[aria-label="Halloween contest prizes"]');
    const votingGallery = entryRoot?.querySelector('[data-hw-voting]')?.closest('section.hw-section');

    if (!storyRoot || !entryRoot || !prizeGallery || !votingGallery) return;

    // Arrange only the prize, current entries, and past entries displays; keep every other block in place.
    if (
      prizeGallery.parentElement !== storyRoot ||
      votingGallery.parentElement !== entryRoot ||
      (pastShowcase && pastShowcase.parentElement !== storyRoot)
    ) return;

    const votingMarker = document.createComment('Halloween voting gallery position');
    votingGallery.replaceWith(votingMarker);

    if (pastShowcase) {
      const pastMarker = document.createComment('Halloween past showcase position');
      const prizeMarker = document.createComment('Halloween prize image position');
      pastShowcase.replaceWith(pastMarker);
      prizeGallery.replaceWith(prizeMarker);
      pastMarker.replaceWith(prizeGallery);
      prizeMarker.replaceWith(votingGallery);
      votingMarker.replaceWith(pastShowcase);
      return;
    }

    prizeGallery.insertAdjacentElement('afterend', votingGallery);
    votingMarker.remove();
  };

  const initShowcases = (scope = document) => {
    scope.querySelectorAll('[data-hw-showcase]').forEach(initShowcase);
  };

  const initVotingSections = (scope = document) => {
    scope.querySelectorAll('[data-hw-voting]').forEach(initVoting);
  };

  const initEntryForms = (scope = document) => {
    scope.querySelectorAll('[data-hw-entry-form]').forEach(initEntryForm);
  };

  const initAllEntriesGalleries = (scope = document) => {
    scope.querySelectorAll('[data-hw-all-entries]').forEach(initAllEntriesGallery);
  };

  initShowcases();
  initVotingSections();
  initEntryForms();
  initAllEntriesGalleries();
  swapShowcaseAndVotingSections();
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('[id^="HalloweenContestStory-"], #HalloweenEntry')) {
      window.location.reload();
      return;
    }

    initShowcases(event.target);
    initVotingSections(event.target);
    initEntryForms(event.target);
    initAllEntriesGalleries(event.target);
    window.requestAnimationFrame(swapShowcaseAndVotingSections);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-hw-voting]').forEach((root) => root.hwVotingCleanup?.());
    event.target.querySelectorAll('[data-hw-entry-form]').forEach((form) => form.hwEntryCleanup?.());
  });

  document.addEventListener('click', async (event) => {
    const galleryTrigger = event.target.closest('.hw-contest [data-hw-entry-gallery-trigger]');
    if (galleryTrigger) {
      event.preventDefault();
      ensureEntryViewer().open(galleryTrigger);
      return;
    }

    const scrollLink = event.target.closest('.hw-contest [data-hw-scroll]');
    if (scrollLink) {
      const selector = scrollLink.getAttribute('href');
      const target = selector && selector.startsWith('#') ? document.querySelector(selector) : null;
      if (target) {
        event.preventDefault();
        target.scrollIntoView({
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
          block: 'start'
        });
      }
      return;
    }

    const copyButton = event.target.closest('.hw-contest [data-hw-copy]');
    if (!copyButton) return;

    const row = copyButton.closest('.hw-code-row');
    const code = row?.querySelector('[data-hw-code]')?.textContent?.trim();
    if (!code) return;

    let copied = false;

    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(code);
      copied = true;
    } catch (error) {
      const textarea = document.createElement('textarea');
      textarea.value = code;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      copied = document.execCommand('copy');
      textarea.remove();
    }

    const originalLabel = copyButton.dataset.label || copyButton.textContent;
    const successLabel = copyButton.dataset.successLabel || 'COPIED';
    const failureLabel = copyButton.dataset.failureLabel || 'TRY AGAIN';
    const status = row?.querySelector('[data-hw-copy-status]');
    copyButton.textContent = copied ? successLabel : failureLabel;
    if (status) {
      status.textContent = copied
        ? `${code} copied to clipboard.`
        : `Could not copy automatically. The code is ${code}.`;
    }

    window.setTimeout(() => {
      copyButton.textContent = originalLabel;
      if (status) status.textContent = '';
    }, 1800);
  });
})();
